/*
  Lightweight, isolated IndexedDB for user notes so schema changes do not
  interfere with the main application DB that is versioned by language.json.
*/

const NOTES_DB_NAME = 'CalcAppNotes';
const NOTES_DB_VERSION = 1; // independent from the main app DB
const NOTES_STORE = 'notes';

let NOTES_DB: IDBDatabase | undefined;

export interface NoteRow {
  id: string;           // UUID
  content: string;      // AES-encrypted note body string
  metadata?: string;    // AES-encrypted JSON string of NoteMetadata
}

export async function getNotesDb(): Promise<IDBDatabase> {
  if (NOTES_DB) {
    return NOTES_DB;
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(NOTES_DB_NAME, NOTES_DB_VERSION);

    request.onerror = (e) => {
      reject((e as any)?.target?.error || 'Failed to open notes DB');
    };

    request.onupgradeneeded = (e) => {
      const db = (e.target as any).result as IDBDatabase;
      if (!db.objectStoreNames.contains(NOTES_STORE)) {
        db.createObjectStore(NOTES_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = (e) => {
      NOTES_DB = (e.target as any).result as IDBDatabase;
      resolve(NOTES_DB);
    };
  });
}

export async function getNoteRow(id: string): Promise<NoteRow | undefined> {
  const db = await getNotesDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([NOTES_STORE], 'readonly');
    const store = tx.objectStore(NOTES_STORE);
    const req = store.get(id);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result as NoteRow | undefined);
  });
}

export async function putNoteRow(row: NoteRow): Promise<void> {
  const db = await getNotesDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([NOTES_STORE], 'readwrite');
    const store = tx.objectStore(NOTES_STORE);
    const req = store.put(row);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
  });
}

export async function deleteNoteRow(id: string): Promise<void> {
  const db = await getNotesDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([NOTES_STORE], 'readwrite');
    const store = tx.objectStore(NOTES_STORE);
    const req = store.delete(id);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
  });
}

export async function getAllNoteRows(): Promise<NoteRow[]> {
  const db = await getNotesDb();
  return new Promise((resolve, reject) => {
    const results: NoteRow[] = [];
    const tx = db.transaction([NOTES_STORE], 'readonly');
    const store = tx.objectStore(NOTES_STORE);
    const req = store.openCursor();
    req.onerror = () => reject(req.error);
    req.onsuccess = (e) => {
      const cursor = (e.target as any).result as IDBCursorWithValue | null;
      if (cursor) {
        results.push(cursor.value as NoteRow);
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve(results);
  });
}


